// ./src/components/features/Connectors/ConfigEditors/AwsDynamoDbConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
  NumberInput,
  Group,
  Box,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsDynamoDbConnectorConfig } from "@/types";

export interface AwsDynamoDbConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AwsDynamoDbConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  tableName: "",
  partitionKey: { name: "pk", value: "{{formId}}" },
  sortKey: { name: "sk", value: "{{requestId}}" },
  ttlAttribute: "",
  ttlDays: 365,
};

// Common AWS regions
const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

/**
 * Parse and validate AWS DynamoDB connector configuration
 */
const parseAwsDynamoDbConfig = (
  jsonString: string
): AwsDynamoDbConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  // Ensure nested objects have defaults
  if (!parsed.partitionKey) {
    parsed.partitionKey = { name: "pk", value: "{{formId}}" };
  }
  if (!parsed.sortKey) {
    parsed.sortKey = { name: "sk", value: "{{requestId}}" };
  }
  return parsed;
};

export const AwsDynamoDbConnectorConfigEditor: React.FC<
  AwsDynamoDbConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsDynamoDbConnectorConfig>(() =>
    parseAwsDynamoDbConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsDynamoDbConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsDynamoDbConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (value: string | null) => {
    updateConfig({ ...config, region: value || "" });
  };

  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, tableName: e.target.value });
  };

  const handlePartitionKeyNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({
      ...config,
      partitionKey: {
        ...config.partitionKey!,
        name: e.target.value,
      },
    });
  };

  const handlePartitionKeyValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({
      ...config,
      partitionKey: {
        ...config.partitionKey!,
        value: e.target.value,
      },
    });
  };

  const handleSortKeyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({
      ...config,
      sortKey: {
        ...config.sortKey!,
        name: e.target.value,
      },
    });
  };

  const handleSortKeyValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({
      ...config,
      sortKey: {
        ...config.sortKey!,
        value: e.target.value,
      },
    });
  };

  const handleTtlAttributeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, ttlAttribute: e.target.value });
  };

  const handleTtlDaysChange = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    updateConfig({ ...config, ttlDays: isNaN(numValue) ? 365 : numValue });
  };

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create access keys in{" "}
          <Anchor
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS IAM Console
          </Anchor>
          {". Manage tables in "}
          <Anchor
            href="https://console.aws.amazon.com/dynamodbv2/"
            target="_blank"
            rel="noopener noreferrer"
          >
            DynamoDB Console
          </Anchor>
        </Text>
      </Alert>

      {/* Access Key ID */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Secret Access Key */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS Secret Access Key"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region where the table is located"
        data={AWS_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
      />

      {/* Table Name */}
      <TextInput
        label="Table Name"
        placeholder="form-submissions"
        description="DynamoDB table name (table must already exist)"
        value={config.tableName}
        onChange={handleTableNameChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Partition Key */}
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Partition Key
        </Text>
        <Group grow>
          <TextInput
            placeholder="pk"
            description="Attribute name"
            value={config.partitionKey?.name || "pk"}
            onChange={handlePartitionKeyNameChange}
            disabled={disabled}
            size="md"
          />
          <TextInput
            placeholder="{{formId}}"
            description="Value pattern"
            value={config.partitionKey?.value || "{{formId}}"}
            onChange={handlePartitionKeyValueChange}
            disabled={disabled}
            size="md"
          />
        </Group>
      </Box>

      {/* Sort Key */}
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Sort Key
        </Text>
        <Group grow>
          <TextInput
            placeholder="sk"
            description="Attribute name"
            value={config.sortKey?.name || "sk"}
            onChange={handleSortKeyNameChange}
            disabled={disabled}
            size="md"
          />
          <TextInput
            placeholder="{{requestId}}"
            description="Value pattern"
            value={config.sortKey?.value || "{{requestId}}"}
            onChange={handleSortKeyValueChange}
            disabled={disabled}
            size="md"
          />
        </Group>
      </Box>

      {/* TTL Configuration */}
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Time to Live (TTL) - Optional
        </Text>
        <Group grow>
          <TextInput
            placeholder="expiresAt"
            description="TTL attribute name (leave empty to disable)"
            value={config.ttlAttribute || ""}
            onChange={handleTtlAttributeChange}
            disabled={disabled}
            size="md"
          />
          <NumberInput
            placeholder="365"
            description="Days until expiration"
            value={config.ttlDays || 365}
            onChange={handleTtlDaysChange}
            disabled={disabled || !config.ttlAttribute}
            size="md"
            min={1}
            max={3650}
          />
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          TTL must be enabled on your DynamoDB table for automatic item
          deletion.
        </Text>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for key values:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>

      {/* Table Schema Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
        <Text size="sm">
          <strong>Important:</strong> Your DynamoDB table must have a key schema
          that matches the configured partition key and sort key attribute
          names. Form data fields will be flattened with underscores (e.g.,{" "}
          <Code>formData_email</Code>).
        </Text>
      </Alert>
    </Stack>
  );
};
